/**
 * ============================================================================================
 * THE SEAM. Which transport this feature talks to is decided ONCE, in this file, at
 * `ACTIVE_TRANSPORT` - and it follows the app's ONE live flag rather than a line to edit.
 * ============================================================================================
 *
 * The four operations this feature calls - `getAuthorSession`, `getPlatformContacts` (milestone
 * A1) and `getCurrentTerms` / `acceptTerms` (A2) - are built and mounted by `authors-api` now
 * (`src/apps/authors-api/authors-api.module.ts`, `:108`), so a build that sets
 * `VITE_AUTHORS_API_LIVE=true` (read by `isLiveApiConfigured()` in src/config.ts, the same reader
 * every other transport in this app uses) gets the fetch transport below. Every other build gets
 * the fixture module, which is what the default build - the one the accessibility leg checks - has
 * always rendered. This file used to hard-code the fixture and call the switch "THE ONE LINE",
 * which meant a live build showed live cabinet screens beside a support page reading fixtures.
 *
 * A transport is deliberately the NARROWEST thing that can be swapped: method, path, body, signal
 * in; status, a parsed body and any `Retry-After` out. It knows nothing about any operation. That
 * is what keeps the swap to one decision rather than to one edit per screen — the typing, the
 * runtime shape checks, the error mapping and every screen state above it are the real ones under
 * either transport.
 *
 * THE BEARER TOKEN comes from the app's one token source, the auth client's current session
 * (`features/auth/session/access-token.ts`). The cabinet is served from `authors.moonx.app` and
 * calls `moonx.art/authors-api/*` CROSS-ORIGIN, BEARER-ONLY, WITH NO COOKIES
 * (`adr/0002-edge-addressing.md`, `adr/0007`) — Firebase sessions are per-origin, so
 * there is no cookie to fall back on and `credentials: 'omit'` below is a correctness statement,
 * not a default.
 *
 * A 401 IS NOT THIS FILE'S TO INTERPRET. The fetch transport runs its `fetch` under the rule all
 * five screen clients share (`features/auth/session/send-with-session.ts`): a bare 401 earns one
 * forced refresh and one retry, and a refused credential ends the session - so the screens'
 * "session expired" block is a moment's state at most before the live gate moves the person on.
 */
import { createContext, useContext } from 'react';
import { AUTHORS_API_BASE_URL, isLiveApiConfigured } from '../../../config';
import { accessToken } from '../../auth/session/access-token';
import { sendWithSession } from '../../auth/session/send-with-session';
import { retryAfterOf } from '../../cabinet/api/problem';
import { ApiError } from './errors';
import type { Problem } from './contract';
import { createFixtureTransport, resolveFixtureScenario } from './fixtures';

export type HttpMethod = 'GET' | 'POST';

export interface ApiCall {
  readonly method: HttpMethod;
  /** Spec path, with the leading slash: `/v1/terms/current`. See `API_PATHS` in ./contract.ts. */
  readonly path: string;
  /** JSON request body. Absent for every GET. */
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

export interface ApiRawResponse {
  readonly status: number;
  /** The parsed JSON body, or `null` for an empty one. Narrowed by ./operations.ts, never before. */
  readonly body: unknown;
  /**
   * `Retry-After` in seconds, when the answer carried one the app's one reader could read
   * (`retryAfterOf`, ../../cabinet/api/problem.ts). ./operations.ts puts a 429's on its error.
   */
  readonly retryAfterSeconds?: number | undefined;
}

export type ApiTransport = (call: ApiCall) => Promise<ApiRawResponse>;

export interface FetchTransportOptions {
  /** Absolute, cross-origin. From src/config.ts, which is the file the deploy smoke check reads. */
  readonly baseUrl: string;
  /**
   * Resolves to the Firebase ID token, or null when there is no session yet. `forceRefresh` is
   * asked for at most once per call, by the shared 401 rule.
   */
  readonly getAccessToken: (forceRefresh?: boolean) => Promise<string | null>;
}

/**
 * The real transport. It is WRITTEN AND TYPECHECKED, just not selected — a seam nobody has compiled
 * is a plan, not a seam.
 */
export function createFetchTransport(options: FetchTransportOptions): ApiTransport {
  return async (call) => {
    // One attempt per token; `acceptTerms` is replayed after a bare 401 like the reads, because a
    // 401 is refused before the acceptance could be written (see the header).
    const response = await sendWithSession(options.getAccessToken, async (token) => {
      const headers: Record<string, string> = {
        // Problem bodies come back as `application/problem+json`, so asking only for
        // `application/json` would be a lie about what this client accepts.
        Accept: 'application/json, application/problem+json',
      };
      if (token !== null) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (call.body !== undefined) {
        headers['Content-Type'] = 'application/json';
      }

      const init: RequestInit = {
        method: call.method,
        headers,
        // Both of this feature's GETs declare `Cache-Control: no-store` on their 200. There is no
        // CDN in front (ADR-0002), so the browser cache is the only cache there is — and asking for
        // `no-store` on the way out means a stale terms state cannot come back from disk if a
        // header is ever dropped by a proxy.
        cache: 'no-store',
        // Bearer-only. Never send or accept cookies: see the header of this file.
        credentials: 'omit',
        mode: 'cors',
        ...(call.body === undefined ? {} : { body: JSON.stringify(call.body) }),
        ...(call.signal === undefined ? {} : { signal: call.signal }),
      };

      try {
        return await fetch(`${options.baseUrl}${call.path}`, init);
      } catch (cause) {
        // DNS, TLS, offline, CORS, or an abort. None of these reached the API, so there is no
        // status and no problem body to report.
        throw new ApiError(
          'network',
          0,
          null,
          cause instanceof Error ? cause.message : 'The request could not be sent.',
        );
      }
    });

    if (response.status === 204) {
      return { status: response.status, body: null };
    }

    let parsed: unknown = null;
    const raw = await response.text();
    if (raw.length > 0) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        // Something answered and it was not JSON. An HTML error page from a proxy lands here, and
        // reporting it as "shape" rather than as the status it carried is the honest answer: the
        // API did not produce this.
        throw new ApiError(
          'shape',
          response.status,
          null,
          'The API answered with something that is not JSON.',
        );
      }
    }

    return {
      status: response.status,
      body: parsed,
      retryAfterSeconds: retryAfterOf(response.headers),
    };
  };
}

/** Narrow a problem body without trusting it. Used by ./operations.ts on every non-2xx. */
export function readProblem(body: unknown): Problem | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }
  const candidate = body as Record<string, unknown>;
  if (
    typeof candidate.type === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.status === 'number' &&
    typeof candidate.error_code === 'string'
  ) {
    return body as Problem;
  }
  return null;
}

/**
 * ============================================================================================
 * THE ONE DECISION.
 *
 *   flag unset:  createFixtureTransport(resolveFixtureScenario())
 *   flag set:    createFetchTransport({ baseUrl: AUTHORS_API_BASE_URL, getAccessToken: accessToken })
 *
 * A function so a spec can assert both branches without reloading the module; the constant below
 * is what the app uses, decided once at load because the context default is decided once too.
 * ============================================================================================
 */
export function createDefaultTransport(live: boolean = isLiveApiConfigured()): ApiTransport {
  return live
    ? createFetchTransport({ baseUrl: AUTHORS_API_BASE_URL, getAccessToken: accessToken })
    : createFixtureTransport(resolveFixtureScenario());
}

export const ACTIVE_TRANSPORT: ApiTransport = createDefaultTransport();

/**
 * True while the screens are talking to fixtures. Screens render a standing notice when it is, so
 * nobody mistakes a fixture address for the operator's real one.
 */
export const IS_FIXTURE_TRANSPORT: boolean = !isLiveApiConfigured();

/** Where the fetch transport points in this build, whichever transport is active. */
export const CONFIGURED_API_BASE_URL = AUTHORS_API_BASE_URL;

/**
 * The override seam for tests and for a story that wants a particular state. The default IS the
 * active transport, so nothing has to wrap the app for it to work.
 */
export const ApiTransportContext = createContext<ApiTransport>(ACTIVE_TRANSPORT);

export function useApiTransport(): ApiTransport {
  return useContext(ApiTransportContext);
}
